<template>
	<div
		class="absolute top-3 right-1.5 left-1.5 flex items-center gap-2.5 overflow-hidden rounded-xl px-3 py-2 transition-all duration-500"
		:class="{
			'bg-fill-tertiary scale-95': !rain.show,
			'bg-fill-secondary': rain.show && rain.status === 'pending',
			'bg-[#2E53CE]': rain.show && rain.status === 'active'
		}"
		@click="rain.open"
	>
		<Transition
			enterActiveClass="transition-opacity duration-500"
			leaveActiveClass="transition-opacity duration-500"
			enterFromClass="opacity-0"
			leaveToClass="opacity-0"
		>
			<div
				class="absolute inset-0 bg-linear-to-r from-[#2E53CE] to-[#2089F3] transition-all! ease-linear"
				:style="{
					width,
					'--tw-duration': duration,
					'transition-duration': duration
				}"
				v-if="rain.show && rain.status === 'active'"
			/>
		</Transition>
		<RainIcon
			class="relative h-7.5 w-6.5"
			:class="{
				'text-[#565656]': rain.status === 'pending',
				'text-[#237BE9]': rain.status === 'active'
			}"
		/>
		<div class="text-label-primary relative font-semibold uppercase">
			{{ t('rain') }}
		</div>
		<div
			class="relative ml-auto inline-flex gap-px overflow-hidden rounded-full"
			v-hover="{ active: 'scale-95' }"
			@click="tip"
			v-if="rain.status === 'pending'"
		>
			<div class="bg-fill-tertiary inline-flex h-8.5 items-center pr-3 pl-2">
				<CoinPrice :price="rain.pool" />
			</div>
			<div class="bg-fill-tertiary inline-flex h-8.5 items-center pr-2.5 pl-2">
				<TipIcon class="text-label-tertiary h-5 w-5" />
			</div>
		</div>
		<div
			class="relative ml-auto inline-flex gap-px overflow-hidden rounded-full"
			:class="{
				'opacity-60': joining
			}"
			v-hover="{ active: 'scale-95' }"
			@click="join"
			v-if="rain.status === 'active' && !rain.joined"
		>
			<div
				class="text-label-primary inline-flex h-8.5 items-center bg-[#2240A4] px-3 text-sm font-semibold uppercase"
			>
				{{ t('join') }}
			</div>
			<div class="inline-flex h-8.5 items-center bg-[#2240A4] pr-3 pl-2">
				<CoinPrice :price="rain.pool" />
			</div>
		</div>
		<CoinPrice
			v-if="rain.status === 'active' && rain.joined"
			class="relative ml-auto"
			:price="rain.pool"
			mono
			:animationDuration="1000"
		/>
		<CloseIcon
			class="text-label-primary relative h-5 w-5 opacity-60 after:absolute after:-inset-2"
			v-hover="{ active: 'scale-95' }"
			@click.stop="rain.close"
		/>
		<RainRequirementsModal v-model="requirementsModal" @retry="join" />
		<RainTipModal v-model="rainTip" />
	</div>
</template>
<script setup lang="ts">
import RainIcon from '@/assets/icons/rain.svg'
import TipIcon from '@/assets/icons/tip.svg'
import CloseIcon from '@/assets/icons/close.svg'
import { useI18n } from 'vue-i18n'
import CoinPrice from './CoinPrice.vue'
import { useRain } from '@/stores/rain'
import RainRequirementsModal from './modals/RainRequirementsModal.vue'
import { computed, nextTick, onUnmounted, ref, watch } from 'vue'
import { client } from '@/api/orpc'
import { useUser } from '@/stores/user'
import RainTipModal from './modals/RainTipModal.vue'
import { sleep } from '@/utils/sleep'

const { t } = useI18n({
	messages: {
		en: {
			rain: 'rain',
			join: 'join'
		},
		ru: {
			rain: 'дождь',
			join: 'вступить'
		}
	}
})

const rain = useRain()
const user = useUser()

const requirementsModal = ref(false)

const openRequirements = () => {
	requirementsModal.value = true
}
const closeRequirements = () => {
	requirementsModal.value = false
}

const joining = ref(false)

const join = async () => {
	if (!rain.show) {
		return
	}
	if (joining.value) {
		return
	}

	joining.value = true

	const { isDefined, error, data } = await client.user.joinRain()

	joining.value = false

	if (isDefined) {
		if (error.code === 'NOT_CHANNEL_SUBSCRIBER') {
			user.setPremiumAndSubscription(user.isPremium, false)
			openRequirements()
			return
		}
		if (error.code === 'NOT_CHAT_MEMBER') {
			user.setChatMember(false)
			openRequirements()
			return
		}
	}

	if (error) {
		return console.error(error)
	}

	if (!data) {
		return
	}

	closeRequirements()

	rain.setJoined(data.joined)
}

const rainTip = ref(false)

const tip = () => {
	if (!rain.show || rainTip.value) {
		return
	}
	rainTip.value = true
}

const startedAt = ref(user.now())
const duration = computed(() => {
	if (!rain.endsAt) {
		return '0ms !important'
	}

	return `${rain.endsAt - startedAt.value}ms !important`
})
const width = ref('100%')

watch(
	() => rain.endsAt,
	async () => {
		if (!rain.endsAt) {
			return
		}

		await user.nowAvailable()

		startedAt.value = user.now()

		await nextTick()
		await sleep(10)

		width.value = '0%'
	},
	{ immediate: true }
)
</script>
